import type {
    MemberTag,
    MemberTagsApiResult,
    TaggedMember,
} from '../../../types'
import { API_TAGS_USERS } from '../../constants/endpoints'
import { parseStyles } from '../../utilities/css'
import type { EdgeApiResponse } from './utilities'

type MemberTagsResponse = EdgeApiResponse<MemberTagsApiResult>

const TAG_LIMIT = 3000
const MEMBER_LIMIT = 100

const request = async (url: string, signal?: AbortSignal) => {
    const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        signal,
    })
    const data = (await response.json()) as MemberTagsResponse
    if (data.err || !data.result) {
        throw Object.assign(new Error(data.errmsg || 'member tags'), {
            err: data.err,
            errmsg: data.errmsg,
        })
    }
    return data.result
}

export const fetchMemberTags = async (
    signal?: AbortSignal,
): Promise<Array<MemberTag>> => {
    const query = new URLSearchParams({
        init: '1',
        mode: 'tags',
        type: 'users',
        limit: String(TAG_LIMIT),
        sort: 'count',
        user_data: '1',
    })
    const result = await request(`${API_TAGS_USERS}?${query}`, signal)
    return (result.tags ?? []).map(({ tag, tag_count }) => ({
        tag,
        count: tag_count,
    }))
}

const colorFromFontStyle = (fontStyle?: string) =>
    (parseStyles(fontStyle ?? '').color ?? '').replace('#', '')

export const fetchMembersByTag = async (
    tag: string,
    signal?: AbortSignal,
): Promise<Array<TaggedMember>> => {
    const query = new URLSearchParams({
        user_data: '1',
        include_tags: '1',
        limit: String(MEMBER_LIMIT),
    })
    const result = await request(
        `${API_TAGS_USERS}/${encodeURIComponent(tag)}?${query}`,
        signal,
    )
    const userData = result.user_data ?? {}
    const users = result.users
        ? result.users.map((id) => userData[id]).filter(Boolean)
        : Object.values(userData)
    return users.map((user) => ({
        id: user.user_id,
        name: user.username,
        color: colorFromFontStyle(user.font_style),
        tags: user.tags ?? [tag],
    }))
}

export const MEMBER_TAG_SLOTS = 32
export const MEMBER_TAG_MAX = 32
export const MEMBER_TAGS_SCRIPT = 'tags.php'

export const sanitizeMemberTag = (value: string) =>
    value
        .replace(/[^\w ]/g, '')
        .slice(0, MEMBER_TAG_MAX)
        .trim()

export const memberTagsSaveFields = (tags: Array<string>) => {
    const fields: Record<string, string | number> = { update: 1 }
    for (let slot = 1; slot <= MEMBER_TAG_SLOTS; slot += 1) {
        fields[`tag${slot}`] = tags[slot - 1] ?? ''
    }
    return fields
}

export const parseMemberTagsPage = (html: string): Array<string> => {
    const doc = new DOMParser().parseFromString(html, 'text/html')
    return Array.from(doc.querySelectorAll<HTMLInputElement>('input.tag'))
        .map((input) => input.value.trim())
        .filter(Boolean)
}

export const memberTagsSaved = (html: string) => html.includes('Tags Saved!')
